import { useCallback, useMemo, useState } from 'react';
import type { Option, StanMultiSelectProps } from '../types';
import { norm } from '@/lib/helpers';
import { useDebounce } from '@/hooks/useDebounce';

export type VisibleOption = Option & { isCreatable?: boolean };

type HookArgs = Pick<StanMultiSelectProps, 'value' | 'options' | 'onChange' | 'allowCreate' | 'disabled' | 'maxTags'>;

type UseStanMultiSelectReturn = {
    query: string;
    setQuery: (value: string) => void;
    visibleOptions: VisibleOption[];
    selectActiveOption: (activeIndex: number) => VisibleOption | undefined;
    addTag: (label: string) => boolean;
    removeTag: (label: string) => void;
    hasReachedLimit: boolean;
};

export function useStanMultiSelect({
    value,
    options,
    onChange,
    allowCreate = true,
    disabled = false,
    maxTags,
}: HookArgs): UseStanMultiSelectReturn {
    const [query, setQuery] = useState('');

    // Debounce query to improve performance during typing
    const debouncedQuery = useDebounce(query, 150);

    const normalizedSelected = useMemo(() => new Set(value.map(norm)), [value]);
    const hasReachedLimit = Boolean(maxTags && value.length >= maxTags);

    const normalizedQuery = norm(debouncedQuery);

    // Filter and sort options based on query, prioritizing exact matches
    // Removes already selected options and sorts by relevance (exact matches first)
    const filteredOptions = useMemo(() => {
        // Early return if no query - show all unselected options
        if (!normalizedQuery) {
            return options.filter(option => !normalizedSelected.has(norm(option.label)));
        }

        const pool = options.filter(option => {
            if (normalizedSelected.has(norm(option.label))) return false;
            return norm(option.label).includes(normalizedQuery);
        });

        // Sort by relevance: startsWith first, then includes
        return pool.sort((a, b) => {
            const aLabel = norm(a.label);
            const bLabel = norm(b.label);
            const aStartsWith = aLabel.startsWith(normalizedQuery);
            const bStartsWith = bLabel.startsWith(normalizedQuery);

            if (aStartsWith && !bStartsWith) return -1;
            if (!aStartsWith && bStartsWith) return 1;
            return 0;
        });
    }, [options, normalizedSelected, normalizedQuery]);

    const canCreate = useMemo(() => {
        return (
            allowCreate &&
            debouncedQuery.trim().length > 0 &&
            !normalizedSelected.has(normalizedQuery) &&
            !options.some(option => norm(option.label) === normalizedQuery)
        );
    }, [allowCreate, debouncedQuery, normalizedSelected, normalizedQuery, options]);

    const visibleOptions: VisibleOption[] = useMemo(() => {
        if (!canCreate) return filteredOptions;
        return [{ id: '__create__', label: debouncedQuery.trim(), isCreatable: true }, ...filteredOptions];
    }, [filteredOptions, canCreate, debouncedQuery]);

    const addTag = useCallback(
        (label: string): boolean => {
            if (disabled || !label.trim()) return false;

            const trimmedLabel = label.trim();

            // If we've reached the limit, replace the existing tag instead of adding
            if (hasReachedLimit && maxTags === 1) {
                onChange([trimmedLabel]);
                setQuery('');
                return true;
            }

            if (hasReachedLimit) return false;

            // Add tag and remove duplicates efficiently
            const next = [...value, trimmedLabel].filter((item, index, arr) => arr.indexOf(item) === index);
            onChange(next);
            setQuery('');
            return true;
        },
        [disabled, hasReachedLimit, maxTags, onChange, value]
    );

    const removeTag = useCallback(
        (label: string) => {
            if (disabled) return;
            onChange(value.filter(entry => norm(entry) !== norm(label)));
        },
        [disabled, onChange, value]
    );

    const selectActiveOption = useCallback(
        (activeIndex: number) => {
            const option = visibleOptions[activeIndex];
            if (!option) return undefined;
            addTag(option.label);
            return option;
        },
        [addTag, visibleOptions]
    );

    return {
        query,
        setQuery,
        visibleOptions,
        selectActiveOption,
        addTag,
        removeTag,
        hasReachedLimit,
    };
}
